import isFunction from 'lodash/isFunction';
import { IDictionary } from '@belong/types';
import { isObject, isString } from '../object';

export type IStrictDictionary = Record<string, string | number | boolean>;
export type IOptionalStringMap = Record<string, string | null | undefined>;
export type IStringMap = Record<string, string>;

export const toCamelCase = (str = ''): string => {
  return str
    .replace(/-/, ' ')
    .replace(/\s(.)/g, (s: string) => s.toUpperCase())
    .replace(/\s/g, '')
    .replace(/^(.)/, (s: string) => s.toLowerCase());
};

export const toSentenceCase = (str = ''): string => `${str.charAt(0).toUpperCase()}${str.slice(1).toLowerCase()}`;

/**
 * Comparator for sorting strings arrays alphabetically
 */
export const sortAlphabetically = (a: string, b: string): number => a.localeCompare(b);

export const getCurrencyStringFromPrice = (price: any): string => {
  if (price) {
    const { unit, value } = price;
    if (!unit || typeof value !== 'number') {
      return '';
    }

    return `${unit}${value}`;
  }

  return '';
};

const LANG = {
  Vietnamese: 'vi',
  Spanish: 'es',
  Arabic: 'ar',
  Thai: 'th',
  Punjabi: 'pa',
  Japanese: 'ja',
  Chinese: 'zh'
} as const;

type LangCode = (typeof LANG)[keyof typeof LANG];

export const getLangFromText = (text = ''): LangCode | undefined => {
  const trimmedText = text.trim();
  if (!trimmedText) {
    return undefined;
  }

  if ([...trimmedText].every(char => char.charCodeAt(0) <= 0x7f)) {
    return undefined;
  }

  const languageMatchers: Array<{ regex: RegExp; lang: LangCode }> = [
    { regex: /[đĐăâêôơưĂÂÊÔƠƯ]/, lang: LANG.Vietnamese },
    { regex: /[áéíóúÁÉÍÓÚñÑ¿¡]/, lang: LANG.Spanish },
    { regex: /[؀-ۿݐ-ݿ]/, lang: LANG.Arabic },
    { regex: /[\u0E00-\u0E7F]/, lang: LANG.Thai },
    { regex: /[\u0A00-\u0A7F]/, lang: LANG.Punjabi },
    { regex: /[぀-ヿㇰ-ㇿｦ-ﾟ]/, lang: LANG.Japanese },
    { regex: /[一-鿿㐀-䶿豈-﫿]/, lang: LANG.Chinese }
  ];

  const match = languageMatchers.find(({ regex }) => regex.test(trimmedText));
  return match?.lang;
};

type DictionaryInterpolator = (str: string) => string;
const isDictionaryInterpolator = (value: any): value is DictionaryInterpolator => isFunction(value);

/**
 * Create an interpolator function that injects values into a given string
 * Empty dictionary values are converted to ''
 * Number dictionary values are converted to string
 */
export const createDictionaryInterpolator = (dictionary: IDictionary, regExp?: RegExp): DictionaryInterpolator => {
  if (regExp) {
    return (str = ''): string => str.replace(regExp, (_, match) => (dictionary[match] ?? '').toString());
  }

  const keys = Object.keys(dictionary);
  return (str = ''): string => {
    return str.replace(/{{([^{}]+)}}/g, (original, token: string) => {
      const matchedKey = keys.find(key => key.toLowerCase() === token.toLowerCase());
      return matchedKey ? (dictionary[token] ?? '').toString() : original;
    });
  };
};

/**
 * Apply dictionary entries to a string
 */
export const applyDictionary = (dictionary: IDictionary, value = ''): string => {
  return createDictionaryInterpolator(dictionary)(value);
};

/**
 * Apply dictionary to object containing strings
 * - Null dictionary values are converted to ''
 * - Undefined dictionary values are converted to ''
 * - Number dictionary values are converted to string
 * - Boolean dictionary values are converted to string
 */
export const applyDictionaryToObject = (dictionary: IDictionary, values: IOptionalStringMap): IStringMap => {
  const injectValues = createDictionaryInterpolator(dictionary);
  // optional values from contentful are coming back as `undefined`, handling falsy values
  return Object.entries(values).reduce(
    (obj, [token, val]) => ({
      ...obj,
      [token]: injectValues(val ?? '')
    }),
    {}
  );
};

export const applyDictionaryRecursively = <T = any>(
  dictionary: IDictionary | DictionaryInterpolator,
  value: any
): T => {
  const injectValues = isDictionaryInterpolator(dictionary) ? dictionary : createDictionaryInterpolator(dictionary);
  if (isString(value)) {
    return injectValues(value) as T;
  }

  if (Array.isArray(value)) {
    return value.map(arrItem => applyDictionaryRecursively(injectValues, arrItem)) as T;
  }

  if (isObject(value)) {
    return Object.entries(value).reduce(
      (acc, [key, val]) => ({
        ...acc,
        [key]: applyDictionaryRecursively(injectValues, val)
      }),
      {}
    ) as T;
  }

  return value as T;
};

/**
 * Mask a string by replacing all chars with asterisks except first and last
 * - removes any whitespace from either end of the string to ensure valid visible chars
 * - specify a pad amount to increase the preserved characters on either side
 * @example
 * hello => h***o
 * hello, 2 => he*lo
 */
export const maskString = (str: string, pad: number = 1): string =>
  (str || '').trim().replace(/./g, (char, idx, arr) => {
    return idx < pad || idx >= arr.length - pad ? char : '*';
  });

/**
 * Mask an email address for privacy when logging
 * - preserves domain suffix
 * - preserves first and last char of each segment around the @
 * - preserves string lengths
 * @example test@example.com => t**t@e*****e.com
 */
export const maskEmail = (email: string): string => {
  if (!email) {
    return '';
  }

  const suffix = /\.(?=[^.]+$).*/.exec(email)?.[0] ?? '';
  const result = email
    .replace(suffix, '')
    .split('@')
    .map(segment => maskString(segment))
    .join('@');
  return `${result}${suffix}`;
};

export const maskToken = (token: string): string => maskString(token, 5).replace(/\*+/, '*'.repeat(10));

/**
 * Preserve length and mask all but last 3 digits.
 * @example +61488888888 => *********888
 */
export const maskMobileNumber = (number: string): string => number.replace(/.(?=...)/g, '*');

export const pluralise = (count: number, string: string, suffix = 's'): string =>
  count === 1 ? string : `${string}${suffix}`;

/**
 * Slices a string without cutting words, ensuring the result is no longer than maxLength.
 * If the string is truncated, it will cut at the last space before maxLength to avoid cutting words.
 * @example
 * // Returns "this is an"
 * sliceWithoutCuttingWords("this is an example", 10);
 *
 */
export const sliceWithoutCuttingWords = (str: string, maxLength: number): string => {
  if (str.length <= maxLength) {
    return str;
  }

  let slicedString = str.slice(0, maxLength);

  const lastSpaceIndex = slicedString.lastIndexOf(' ');
  if (lastSpaceIndex > -1) {
    slicedString = slicedString.slice(0, lastSpaceIndex);
  }

  return slicedString;
};
